import { sentryService } from "@shared/services/sentry";
import { setSessionBridge, type SessionBridge } from "@shared/api/providers";

/**
 * Cross-cutting hooks the API client fires, plus the one-time wiring the
 * transport needs from the app.
 *
 * Both the session accessors and the unauthorized handler are *injected*
 * rather than imported: reading the store or the router from here would make
 * `shared/api` depend on `@app/*`. `setupInterceptors` is called once, from
 * `main.tsx`.
 */

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

export interface InterceptorSetup {
  /**
   * How the transport reads and renews the session. Without it the client
   * still works, it just sends every request unauthenticated.
   */
  session?: SessionBridge;
  /**
   * Called when a request fails with 401 (or 406) and token renewal could not
   * recover it. Expected to clear the auth store, drop the query cache and
   * send the user to login.
   */
  onUnauthorized?: UnauthorizedHandler;
}

export const setupInterceptors = (setup: InterceptorSetup = {}) => {
  if (setup.session) {
    setSessionBridge(setup.session);
  }
  unauthorizedHandler = setup.onUnauthorized ?? null;
  // Distributed tracing headers (sentry-trace + baggage) are injected
  // automatically by browserTracingIntegration() for fetch calls that match
  // tracePropagationTargets — no manual setup required.
};

/** Test seam. */
export const resetInterceptors = () => {
  unauthorizedHandler = null;
};

export function onUnauthorized(): void {
  unauthorizedHandler?.();
}

export function onApiRequest(method: string, url: string): void {
  sentryService.addBreadcrumb({
    category: "http",
    message: `${method} ${url}`,
    level: "info",
  });
}

export function onApiError(err: unknown, url: string): void {
  sentryService.captureException(err, `API ${url}`);
}
